import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link, Navigate, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { AnchorControls } from '../components/anchors/AnchorControls'
import { Timeline } from '../components/anchors/Timeline'
import { LanguageSwitcher } from '../components/i18n/LanguageSwitcher'
import { NotesOverlay } from '../components/personal/NotesOverlay'
import { useI18n } from '../i18n'
import type { MessageKey } from '../i18n/messages/en'
import {
  findTechnique,
  flattenTechniques,
  getKata,
  techniqueDisplayName,
  techniqueScriptLang,
  techniqueScriptName,
  type KataId,
} from '../data/catalog'
import { kataBannerBackground } from '../lib/theme/kataBanner'
import { formatTimestamp, parseTimestamp } from '../lib/youtube/timeFormat'
import { YoutubePlayer, type PlayerStatus, type YoutubePlayerHandle } from '../lib/youtube/YoutubePlayer'
import { useUnsavedGuard } from '../lib/session/unsavedGuard'
import {
  findOverlappingSegments,
  suggestedUnsetSegment,
  validateSegment,
} from '../lib/validation/segment'
import {
  clearTechniqueSegment,
  getActiveReference,
  upsertTechniqueSegment,
} from '../storage/repositories/references'
import { listFavouritesForKata } from '../storage/repositories/personal'
import type { VideoReference } from '../storage/schema'
import styles from './AnchorEditor.module.css'

const DEFAULT_START = 0
const DEFAULT_END = 10

type SaveSuccessState = {
  key: 'anchors.saved' | 'anchors.savedPending' | 'anchors.cleared'
  overlapNames?: string
} | null

function translateValidationError(
  error: string,
  t: (key: MessageKey, vars?: Record<string, string | number>) => string,
): string {
  if (error === 'Start must be before End.') return t('anchors.startBeforeEnd')
  if (error === 'End must be after Start.') return t('anchors.endAfterStart')
  if (error.startsWith('Start must be within the video duration')) {
    return t('anchors.startWithinDuration')
  }
  if (error.startsWith('End must be within the video duration')) {
    return t('anchors.endWithinDuration')
  }
  if (error === 'Both Start and End are required to configure a segment.') {
    return t('anchors.setBothBeforeSave')
  }
  return error
}

export function AnchorEditor() {
  const { t, locale } = useI18n()
  const { kataId } = useParams<{ kataId: string }>()
  const [searchParams, setSearchParams] = useSearchParams()
  const kata = kataId ? getKata(kataId as KataId) : undefined
  const navigate = useNavigate()
  const playerRef = useRef<YoutubePlayerHandle>(null)
  const previewToken = useRef(0)

  const [reference, setReference] = useState<VideoReference | null>(null)
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState(false)
  const [favouriteIds, setFavouriteIds] = useState<Set<string>>(new Set())

  const techniques = useMemo(() => (kata ? flattenTechniques(kata) : []), [kata])
  const techniqueParam = searchParams.get('technique')
  const selectedId =
    techniqueParam && techniques.some((t) => t.id === techniqueParam)
      ? techniqueParam
      : techniques[0]?.id ?? null

  const selected = selectedId && kata ? findTechnique(kata, selectedId) : undefined

  const [startSeconds, setStartSeconds] = useState(DEFAULT_START)
  const [endSeconds, setEndSeconds] = useState(DEFAULT_END)
  const [startText, setStartText] = useState(formatTimestamp(DEFAULT_START))
  const [endText, setEndText] = useState(formatTimestamp(DEFAULT_END))
  const [startFieldError, setStartFieldError] = useState<string | null>(null)
  const [endFieldError, setEndFieldError] = useState<string | null>(null)
  const [currentTime, setCurrentTime] = useState(0)
  const [duration, setDuration] = useState<number | null>(null)
  const [playerStatus, setPlayerStatus] = useState<PlayerStatus>('idle')
  const [playerError, setPlayerError] = useState<string | null>(null)
  const [dirty, setDirty] = useState(false)
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState<string | null>(null)
  const [saveSuccess, setSaveSuccess] = useState<SaveSuccessState>(null)
  const [baseline, setBaseline] = useState<{ start: number; end: number } | null>(null)
  useUnsavedGuard(dirty)

  const refreshFavourites = useCallback(async (id: KataId) => {
    const favs = await listFavouritesForKata(id)
    setFavouriteIds(new Set(favs.map((f) => f.techniqueId)))
  }, [])

  useEffect(() => {
    if (!kata) return
    let cancelled = false
    ;(async () => {
      try {
        const ref = await getActiveReference(kata.id)
        if (cancelled) return
        setReference(ref ?? null)
        await refreshFavourites(kata.id)
        setLoading(false)
      } catch {
        if (cancelled) return
        setLoadError(true)
        setLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [kata, refreshFavourites])

  useEffect(() => {
    previewToken.current += 1
    playerRef.current?.pause()

    if (!reference || !selectedId) return

    const existing = reference.segments.find((s) => s.techniqueId === selectedId)
    const suggested = existing
      ? { startSeconds: existing.startSeconds, endSeconds: existing.endSeconds }
      : suggestedUnsetSegment({
          techniqueId: selectedId,
          orderedTechniqueIds: techniques.map((tech) => tech.id),
          segments: reference.segments,
          durationSeconds: duration,
        })
    const start = suggested.startSeconds
    const end = suggested.endSeconds
    setStartSeconds(start)
    setEndSeconds(end)
    setStartText(formatTimestamp(start))
    setEndText(formatTimestamp(end))
    setStartFieldError(null)
    setEndFieldError(null)
    setBaseline(existing ? { start, end } : null)
    setDirty(false)
    setSaveSuccess(null)
    setSaveError(null)
    playerRef.current?.seekTo(start, true)
    setCurrentTime(start)
    // duration is read when selecting; a separate effect applies it once the player reports length
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional: do not reset edits when duration arrives
  }, [reference, selectedId, techniques])

  /**
   * Once duration is known, preset Start/End for unset techniques.
   * Skip while dirty so the first "Set End Here" is not overwritten back to
   * the video duration (that forced a second click).
   */
  useEffect(() => {
    if (!reference || !selectedId) return
    if (!duration || duration <= 0) return
    if (baseline) return
    if (dirty) return

    const suggested = suggestedUnsetSegment({
      techniqueId: selectedId,
      orderedTechniqueIds: techniques.map((tech) => tech.id),
      segments: reference.segments,
      durationSeconds: duration,
    })
    setStartSeconds(suggested.startSeconds)
    setEndSeconds(suggested.endSeconds)
    setStartText(formatTimestamp(suggested.startSeconds))
    setEndText(formatTimestamp(suggested.endSeconds))
  }, [duration, baseline, dirty, reference, selectedId, techniques])

  useEffect(() => {
    if (!dirty) return
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault()
      e.returnValue = ''
    }
    window.addEventListener('beforeunload', onBeforeUnload)
    return () => window.removeEventListener('beforeunload', onBeforeUnload)
  }, [dirty])

  const markDirty = useCallback(
    (start: number, end: number) => {
      setStartSeconds(start)
      setEndSeconds(end)
      setStartText(formatTimestamp(start))
      setEndText(formatTimestamp(end))
      setStartFieldError(null)
      setEndFieldError(null)
      if (baseline && baseline.start === start && baseline.end === end) {
        setDirty(false)
      } else {
        setDirty(true)
      }
      setSaveSuccess(null)
    },
    [baseline],
  )

  const seekTo = useCallback((seconds: number) => {
    playerRef.current?.seekTo(seconds, true)
    setCurrentTime(seconds)
  }, [])

  const playerReady =
    playerStatus === 'ready' ||
    playerStatus === 'playing' ||
    playerStatus === 'paused' ||
    playerStatus === 'ended'
  const isPlaying = playerStatus === 'playing'
  const durationPending = !(duration !== null && duration > 0)

  const overlaps = useMemo(() => {
    if (!selectedId || !reference) return []
    return findOverlappingSegments(
      { techniqueId: selectedId, startSeconds, endSeconds },
      reference.segments,
    )
  }, [reference, selectedId, startSeconds, endSeconds])

  const overlapNames = overlaps
    .map((o) => findTechnique(kata!, o.techniqueId)?.name ?? o.techniqueId)
    .join(', ')

  const applyStartText = () => {
    const parsed = parseTimestamp(startText)
    if (!parsed.ok) {
      setStartFieldError(parsed.error)
      return
    }
    setStartFieldError(null)
    if (parsed.seconds >= endSeconds) {
      setStartFieldError(t('anchors.startBeforeEnd'))
      return
    }
    if (duration && parsed.seconds > duration) {
      setStartFieldError(t('anchors.startWithinDuration'))
      return
    }
    markDirty(parsed.seconds, endSeconds)
    seekTo(parsed.seconds)
  }

  const applyEndText = () => {
    const parsed = parseTimestamp(endText)
    if (!parsed.ok) {
      setEndFieldError(parsed.error)
      return
    }
    setEndFieldError(null)
    if (parsed.seconds <= startSeconds) {
      setEndFieldError(t('anchors.endAfterStart'))
      return
    }
    if (duration && parsed.seconds > duration) {
      setEndFieldError(t('anchors.endWithinDuration'))
      return
    }
    markDirty(startSeconds, parsed.seconds)
    seekTo(parsed.seconds)
  }

  const handlePreview = () => {
    const validation = validateSegment({
      startSeconds,
      endSeconds,
      durationSeconds: duration,
    })
    if (!validation.ok || 'unconfigured' in validation) {
      setSaveError(
        !validation.ok
          ? translateValidationError(validation.error, t)
          : t('anchors.setBothFirst'),
      )
      return
    }

    const token = ++previewToken.current
    seekTo(validation.startSeconds)
    playerRef.current?.play()

    const watch = window.setInterval(() => {
      if (token !== previewToken.current) {
        window.clearInterval(watch)
        return
      }
      const t = playerRef.current?.getCurrentTime() ?? 0
      setCurrentTime(t)
      if (t >= validation.endSeconds - 0.15) {
        playerRef.current?.pause()
        playerRef.current?.seekTo(validation.endSeconds, true)
        setCurrentTime(validation.endSeconds)
        window.clearInterval(watch)
      }
    }, 100)
  }

  const handleSave = async () => {
    setSaveError(null)
    setSaveSuccess(null)
    if (!reference || !selectedId) return

    const validation = validateSegment({
      startSeconds,
      endSeconds,
      durationSeconds: duration,
    })
    if (!validation.ok) {
      setSaveError(translateValidationError(validation.error, t))
      return
    }
    if ('unconfigured' in validation) {
      setSaveError(t('anchors.setBothBeforeSave'))
      return
    }

    setSaving(true)
    try {
      const saved = await upsertTechniqueSegment(reference.id, {
        techniqueId: selectedId,
        startSeconds: validation.startSeconds,
        endSeconds: validation.endSeconds,
      })
      setReference(saved)
      setBaseline({ start: validation.startSeconds, end: validation.endSeconds })
      setDirty(false)
      setSaveSuccess({
        key: validation.durationPending ? 'anchors.savedPending' : 'anchors.saved',
        overlapNames: overlaps.length > 0 ? overlapNames : undefined,
      })
    } catch {
      setSaveError(t('anchors.saveFailed'))
    } finally {
      setSaving(false)
    }
  }

  const handleClear = async () => {
    if (!reference || !selectedId) return
    const hasSaved = reference.segments.some((s) => s.techniqueId === selectedId)
    if (!hasSaved && !dirty) return
    if (!window.confirm(t('anchors.clearConfirm'))) {
      return
    }
    setSaving(true)
    setSaveError(null)
    try {
      const saved = await clearTechniqueSegment(reference.id, selectedId)
      setReference(saved)
      const suggested = suggestedUnsetSegment({
        techniqueId: selectedId,
        orderedTechniqueIds: techniques.map((tech) => tech.id),
        segments: saved.segments,
        durationSeconds: duration,
      })
      setBaseline(null)
      setStartSeconds(suggested.startSeconds)
      setEndSeconds(suggested.endSeconds)
      setStartText(formatTimestamp(suggested.startSeconds))
      setEndText(formatTimestamp(suggested.endSeconds))
      setDirty(false)
      setSaveSuccess({ key: 'anchors.cleared' })
    } catch {
      setSaveError(t('anchors.clearFailed'))
    } finally {
      setSaving(false)
    }
  }

  const handleCancel = () => {
    if (dirty && !window.confirm(t('anchors.discardConfirm'))) return
    if (baseline) {
      markDirty(baseline.start, baseline.end)
      setDirty(false)
    } else {
      const suggested = suggestedUnsetSegment({
        techniqueId: selectedId ?? '',
        orderedTechniqueIds: techniques.map((tech) => tech.id),
        segments: reference?.segments ?? [],
        durationSeconds: duration,
      })
      setStartSeconds(suggested.startSeconds)
      setEndSeconds(suggested.endSeconds)
      setStartText(formatTimestamp(suggested.startSeconds))
      setEndText(formatTimestamp(suggested.endSeconds))
      setDirty(false)
    }
    navigate(`/kata/${kataId}`)
  }

  const selectTechnique = (id: string) => {
    if (dirty && !window.confirm(t('anchors.discardTechniqueConfirm'))) return
    setSearchParams({ technique: id })
  }

  if (!kataId || !kata) {
    return <Navigate to="/" replace />
  }

  if (loading) {
    return (
      <div className="app-shell">
        <p className="banner banner-info" role="status">
          {t('common.loading')}
        </p>
      </div>
    )
  }

  if (!reference) {
    return (
      <div className="app-shell">
        <div className="page-header">
          <Link to={`/kata/${kata.id}`} className="btn btn-ghost">
            {t('common.back')}
          </Link>
          <h1>{t('anchors.editorTitle')}</h1>
          <LanguageSwitcher compact />
        </div>
        <div className="surface" style={{ padding: 20 }}>
          <p>{t('anchors.needVideo')}</p>
          <Link to={`/kata/${kata.id}/setup`} className="btn btn-primary">
            {t('anchors.configureVideo')}
          </Link>
        </div>
      </div>
    )
  }

  const configuredCount = reference.segments.length
  const hasSavedSegment = Boolean(
    selectedId && reference.segments.some((s) => s.techniqueId === selectedId),
  )

  return (
    <div className="app-shell">
      <div className="page-header">
        <Link
          to={`/kata/${kata.id}`}
          className="btn btn-ghost"
          onClick={(e) => {
            if (dirty && !window.confirm(t('anchors.discardConfirm'))) {
              e.preventDefault()
            }
          }}
        >
          {t('common.back')}
        </Link>
        <h1>{t('anchors.pageTitle')}</h1>
        <LanguageSwitcher compact />
        <Link to={`/kata/${kata.id}/setup`} className="btn btn-secondary">
          {t('anchors.changeRemoveVideo')}
        </Link>
      </div>

      <div
        className={styles.hero}
        style={{
          background: kataBannerBackground(kata.theme),
          color: kata.theme.text,
        }}
      >
        <span>
          {t('anchors.heroMeta', {
            kata: `${kata.nameJa} (${kata.name})`,
            configured: configuredCount,
            total: techniques.length,
          })}
        </span>
        {selected ? (
          <>
            <strong lang={techniqueScriptLang(selected, locale)}>
              {techniqueScriptName(selected, locale)}
            </strong>
            <span className={styles.heroRomaji}>{selected.name}</span>
          </>
        ) : (
          <strong>{t('common.technique')}</strong>
        )}
      </div>

      {loadError && (
        <p className="banner banner-error" role="alert">
          {t('anchors.loadError')}
        </p>
      )}

      <div className={styles.workspace}>
        <aside className={`surface ${styles.sidebar}`}>
          <h2 className={styles.sideTitle}>{t('anchors.allTechniques')}</h2>
          <p className={styles.sideHint}>{t('anchors.sideHint')}</p>
          <ul className={styles.techList}>
            {techniques.map((tech) => {
              const seg = reference.segments.find((s) => s.techniqueId === tech.id)
              const isSelected = tech.id === selectedId
              const fav = favouriteIds.has(tech.id)
              return (
                <li key={tech.id}>
                  <button
                    type="button"
                    className={`${styles.techBtn} ${isSelected ? styles.techSelected : ''}`}
                    aria-pressed={isSelected}
                    onClick={() => selectTechnique(tech.id)}
                  >
                    <span className={styles.techLabel}>
                      <span
                        className={styles.techName}
                        lang={techniqueScriptLang(tech, locale)}
                      >
                        {fav ? '★ ' : ''}
                        {techniqueScriptName(tech, locale)}
                      </span>
                      <span className={styles.nameRomaji}>{tech.name}</span>
                      {seg ? (
                        <span className={styles.times}>
                          {formatTimestamp(seg.startSeconds)}–{formatTimestamp(seg.endSeconds)}
                        </span>
                      ) : (
                        <span className={styles.badge}>{t('common.unset')}</span>
                      )}
                    </span>
                  </button>
                </li>
              )
            })}
          </ul>
        </aside>

        <section className={`surface ${styles.main}`}>
          {playerError && (
            <p className="banner banner-error" role="alert">
              {playerError}
            </p>
          )}

          {durationPending && (
            <p className="banner banner-warning" role="status">
              {t('anchors.durationPending')}
            </p>
          )}

          {overlaps.length > 0 && (
            <p className="banner banner-warning" role="status">
              {t('anchors.overlapWarning', { names: overlapNames })}
            </p>
          )}

          {/* Player + anchors side by side in one section; controls stay outside stickyMedia. */}
          <div className={styles.editorRow}>
            <div className={styles.stickyMedia}>
              <YoutubePlayer
                ref={playerRef}
                videoId={reference.videoId}
                title={reference.title}
                onReady={(d) => setDuration(d > 0 ? d : null)}
                onTime={setCurrentTime}
                onStateChange={setPlayerStatus}
                onError={setPlayerError}
              />

              <Timeline
                duration={duration}
                currentTime={currentTime}
                startSeconds={startSeconds}
                endSeconds={endSeconds}
                onChangeAnchors={markDirty}
                onSeekPlayhead={seekTo}
                disabled={!playerReady}
              />
            </div>

            <div className={styles.controlsColumn}>
              <AnchorControls
                startSeconds={startSeconds}
                endSeconds={endSeconds}
                startText={startText}
                endText={endText}
                onStartTextChange={(v) => {
                  setStartText(v)
                  setDirty(true)
                  setStartFieldError(null)
                }}
                onEndTextChange={(v) => {
                  setEndText(v)
                  setDirty(true)
                  setEndFieldError(null)
                }}
                onApplyStartText={applyStartText}
                onApplyEndText={applyEndText}
                onSetStartHere={() => {
                  const at = playerRef.current?.getCurrentTime() ?? currentTime
                  if (at >= endSeconds) {
                    setStartFieldError(t('anchors.startBeforeEnd'))
                    return
                  }
                  setStartFieldError(null)
                  markDirty(at, endSeconds)
                }}
                onSetEndHere={() => {
                  const at = playerRef.current?.getCurrentTime() ?? currentTime
                  if (at <= startSeconds) {
                    setEndFieldError(t('anchors.endAfterStart'))
                    return
                  }
                  setEndFieldError(null)
                  markDirty(startSeconds, at)
                }}
                onJumpStart={() => seekTo(startSeconds)}
                onJumpEnd={() => seekTo(endSeconds)}
                onSeek={(delta) => {
                  const at = (playerRef.current?.getCurrentTime() ?? currentTime) + delta
                  const max = duration && duration > 0 ? duration : Number.POSITIVE_INFINITY
                  seekTo(Math.max(0, Math.min(at, max)))
                }}
                onPlayPause={() => {
                  if (isPlaying) playerRef.current?.pause()
                  else playerRef.current?.play()
                }}
                onPreview={handlePreview}
                onClear={() => void handleClear()}
                clearDisabled={saving || (!hasSavedSegment && !dirty)}
                isPlaying={isPlaying}
                playerReady={playerReady}
                startError={startFieldError}
                endError={endFieldError}
              />
            </div>
          </div>

          <div className={styles.sessionBar}>
            {dirty ? (
              <span className="banner banner-warning" role="status">
                {t('anchors.unsaved')}
              </span>
            ) : saveSuccess ? (
              <span className="banner banner-success" role="status">
                {t(saveSuccess.key)}
                {saveSuccess.overlapNames
                  ? t('anchors.overlapNote', { names: saveSuccess.overlapNames })
                  : null}
              </span>
            ) : (
              <span className="banner banner-info" role="status">
                {t('anchors.boundTo', { videoId: reference.videoId })}
              </span>
            )}
            <div className={styles.sessionActions}>
              <button type="button" className="btn btn-secondary" onClick={handleCancel} disabled={saving}>
                {t('common.cancel')}
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => void handleSave()}
                disabled={saving || !dirty}
              >
                {saving ? t('common.saving') : t('common.save')}
              </button>
            </div>
          </div>

          {saveError && (
            <p className="banner banner-error" role="alert">
              {saveError}
            </p>
          )}
        </section>
      </div>

      {selected && (
        <NotesOverlay
          kataId={kata.id}
          techniqueId={selected.id}
          techniqueName={techniqueDisplayName(selected, locale)}
          onFavouriteChange={(id, fav) => {
            setFavouriteIds((prev) => {
              const next = new Set(prev)
              if (fav) next.add(id)
              else next.delete(id)
              return next
            })
          }}
        />
      )}

      {/* Desktop-friendly overview table of all technique timings */}
      <div className={`surface ${styles.tableWrap}`}>
        <h2 className={styles.tableTitle}>{t('anchors.overview')}</h2>
        <div className={styles.tableScroll}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th scope="col">{t('common.technique')}</th>
                <th scope="col">{t('common.start')}</th>
                <th scope="col">{t('common.end')}</th>
                <th scope="col">{t('common.status')}</th>
              </tr>
            </thead>
            <tbody>
              {techniques.map((tech) => {
                const seg = reference.segments.find((s) => s.techniqueId === tech.id)
                return (
                  <tr
                    key={tech.id}
                    className={tech.id === selectedId ? styles.rowSelected : undefined}
                  >
                    <td>
                      <button
                        type="button"
                        className={styles.tableLink}
                        onClick={() => selectTechnique(tech.id)}
                      >
                        {favouriteIds.has(tech.id) ? '★ ' : ''}
                        <span lang={techniqueScriptLang(tech, locale)}>
                          {techniqueScriptName(tech, locale)}
                        </span>
                        <span className={styles.tableRomaji}> {tech.name}</span>
                      </button>
                    </td>
                    <td>{seg ? formatTimestamp(seg.startSeconds) : '—'}</td>
                    <td>{seg ? formatTimestamp(seg.endSeconds) : '—'}</td>
                    <td>{seg ? t('common.configured') : t('common.unset')}</td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </div>

      <p className="disclaimer">{t('anchors.disclaimer')}</p>
    </div>
  )
}
